<template>
    <AppLayout
        ref="appLayout"
        page="/inbox"
        pageTitle="Inbox"
        fabIcon="edit_note"
        :hasFab="can('Create conversations') && !selectedConversation"
        @fabAction="handleCreateMessage()"
        @messageReceived="messageReceived"
    >
        <template #title>
            <div class="inbox-title flex items-center">
                <v-menu
                    v-if="
                        can('Use CRM') &&
                        $page.props.staffView &&
                        $page.props.auth.user.selected_location.crm_enabled
                    "
                    offset-y
                    min-width="200"
                >
                    <template v-slot:activator="{ on }">
                        <v-badge
                            dot
                            overlap
                            :class="{
                                'hide-badge': !unreadOnly && !draftsOnly,
                            }"
                        >
                            <button
                                type="button"
                                class="cursor-pointer flex gap-1 items-center"
                                v-on="on"
                            >
                                {{ crmMode ? "Studio Inbox" : "My Inbox" }}
                                <v-btn small icon :loading="settingsLoading">
                                    <v-icon>keyboard_arrow_down</v-icon>
                                </v-btn>
                            </button>
                        </v-badge>
                    </template>
                    <v-list dense>
                        <v-list-item
                            v-for="(setting, idx) in inboxSettings"
                            :key="setting.name"
                            class="gap-2"
                            @click="setting.action()"
                        >
                            <v-list-item-content class="w-min">
                                <v-list-item-title>{{
                                    setting.label
                                }}</v-list-item-title>
                                <v-list-item-subtitle
                                    >{{ setting.description }}
                                </v-list-item-subtitle></v-list-item-content
                            >
                            <v-list-item-action>
                                <v-icon
                                    :color="setting.value ? 'accent' : null"
                                    >{{
                                        setting.value
                                            ? "toggle_on"
                                            : "toggle_off"
                                    }}</v-icon
                                >
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <div v-else>Inbox</div>
                <v-spacer />
                <SoliBtn
                    v-if="
                        can('Create conversations') &&
                        $vuetify.breakpoint.mdAndUp
                    "
                    round
                    small
                    icon="edit_note"
                    gradient="gradient-orange"
                    class="inbox-header-button"
                    @click="handleCreateMessage()"
                />
            </div>
        </template>

        <v-container
            :fluid="$page.props.staffView"
            class="chat-container h-full py-0 px-3 animate__animated animate__fadeIn"
        >
            <v-row class="chat-row h-full">
                <v-col cols="12" class="chat-index-col flex grow relative py-0">
                    <ConversationIndex
                        ref="conversationIndex"
                        id="chat-index"
                        v-model="selectedConversation"
                        :index="index"
                        :thread="selectedThread"
                        :crmMode="crmMode"
                        :searching="searching"
                        :key="searching"
                        showSearch
                        @input="handleSelectConversation($event)"
                    />
                </v-col>

                <v-col
                    v-if="selectedConversation"
                    id="chat-window"
                    class="pa-0"
                >
                    <ConversationShow
                        ref="conversationShow"
                        :key="conversationKey"
                        :conversation="selectedConversation"
                        :conversationUser="conversationUser"
                        :thread="selectedThread"
                        :messages="selectedConversation?.create ? [] : messages"
                        :messageCount="messageCount"
                        :crmClient="crmClient"
                        :errors="errors"
                        :newMessageAvailable="newMessageAvailable"
                        :crmMode="crmMode"
                        :crmLoading="crmLoading"
                        :action="action"
                        :searching="searching"
                        :cursor="index?.currentCursor"
                        @drafted="updateDraft($event)"
                        @showMessage="$refs.appLayout.showMessage = true"
                        @changeThread="selectedThread = $event"
                        @handleUnread="handleUnread($event)"
                        @openCrm="
                            handleSelectConversation(selectedConversation, true)
                        "
                        @reset="closeModal(() => (selectedConversation = null))"
                    />
                </v-col>

                <v-col
                    v-if="!selectedConversation && $vuetify.breakpoint.mdAndUp"
                    class="flex justify-center align-center"
                >
                    <v-list class="text-align-center">
                        <v-icon x-large class="my-2" color="accent"
                            >chat_bubble_outline</v-icon
                        >
                        <v-list-item-title>No chat selected</v-list-item-title>
                        <v-list-item-subtitle
                            >Please select a conversation</v-list-item-subtitle
                        >
                    </v-list>
                </v-col>
            </v-row>
        </v-container>
    </AppLayout>
</template>

<script>
import AppLayout from "../Layouts/AppLayout.vue";
import ConversationShow from "../Components/Conversation/ConversationShow.vue";
import ConversationIndex from "../Components/Conversation/ConversationIndex.vue";
import SoliResponsiveDialog from "../Components/Base/SoliResponsiveDialog.vue";
import CRMDrawer from "../Layouts/CRMDrawer.vue";
import { encodeInboxSettings } from "../Composables/useInboxSettings.js";

export default {
    layout: CRMDrawer,
    components: {
        AppLayout,
        ConversationShow,
        ConversationIndex,
        SoliResponsiveDialog,
    },
    inject: ["getCrmClient", "closeCrm"],
    props: {
        index: {
            type: Object,
            default: () => ({ conversations: [] }),
        },
        conversation: {
            type: Object,
            default: () => ({}),
        },
        thread: {
            type: Object,
            default: () => ({}),
        },
        messages: {
            type: Array,
            default: () => [],
        },
        newMessageAvailable: {
            type: Object,
            default: () => null,
        },
        messageCount: {
            type: Number,
            default: 0,
        },
        errors: {
            type: Object,
            default: () => {},
        },
        crmMode: {
            type: Boolean,
            default: false,
        },
        crmClient: {
            type: Object,
            default: () => {},
        },
        action: {
            type: String,
            default: "",
        },
        threadTexts: {
            type: Boolean,
            default: false,
        },
        unreadOnly: {
            type: Boolean,
            default: false,
        },
        draftsOnly: {
            type: Boolean,
            default: false,
        },
        searching: {
            type: String,
            default: "",
        },
    },
    data: function () {
        return {
            settingsLoading: false,
            crmLoading: false,
            keyedConversation:
                this.index.conversations.find(
                    (e) => e.id == this.conversation?.id
                ) ?? null,
            selectedConversation:
                this.index.conversations.find(
                    (e) => e.id == this.conversation?.id
                ) ?? null,
            selectedThread: this.thread,
        };
    },
    computed: {
        conversationKey() {
            if (this.selectedConversation.create) {
                return "createMessage";
            }

            // Thread must be "selectedThread", otherwise change won't fire until server loads new thread property
            if (this.selectedThread?.id) {
                return this.selectedThread.id;
            }

            if (this.selectedConversation.message_id) {
                return this.selectedConversation.message_id;
            }

            // Thread must be "selectedConversation", otherwise change won't fire until server loads new conversation property
            if (this.selectedConversation?.id) {
                return this.selectedConversation.id;
            }
            return "";
        },
        conversationUser() {
            return this.conversation?.conversation_user;
        },
        inboxSettings() {
            return [
                {
                    name: "crmMode",
                    label: "Studio Inbox",
                    description: "View inbox as location account",
                    value: this.crmMode,
                    action: () => this.changeCRMView(!this.crmMode),
                    hidden: false,
                },
                {
                    name: "unreadOnly",
                    label: "Filter Unread",
                    description: "View only unread conversations",
                    value: this.unreadOnly,
                    action: () => this.changeUnreadOnly(!this.unreadOnly),
                    hidden: false,
                },
                {
                    name: "draftsOnly",
                    label: "Filter Drafts",
                    description: "View only drafts",
                    value: this.draftsOnly,
                    action: () => this.changeDraftsOnly(!this.draftsOnly),
                    hidden: false,
                },
                {
                    name: "threadTexts",
                    label: "Thread Texts",
                    description: "View text messages as a single thread",
                    value: this.threadTexts,
                    action: () => this.changeThreadTexts(!this.threadTexts),
                    hidden: !this.crmMode,
                },
            ].filter((e) => !e.hidden);
        },
    },
    methods: {
        updateDraft(value) {
            this.selectedConversation.hasDraft = value;
        },
        handleUnread(item) {
            this.$refs.conversationIndex?.markUnread(item);
        },
        handleCreateMessage() {
            this.$inertia.get(
                route("conversation.show"),
                { crmMode: Number(this.crmMode) },
                {
                    preserveState: true,
                    only: [
                        "conversation",
                        "thread",
                        "messages",
                        "newMessageAvailable",
                        "messageCount",
                        "crmClient",
                    ],
                }
            );
            this.selectedConversation = { create: true };
        },
        messageReceived(message) {
            this.$refs.conversationShow?.messageReceived(message);
        },
        getShortCode({ crmMode, unreadOnly, draftsOnly, threadTexts }) {
            return encodeInboxSettings({
                crmMode: crmMode ?? this.crmMode,
                unreadOnly: unreadOnly ?? this.unreadOnly,
                draftsOnly: draftsOnly ?? this.draftsOnly,
                threadTexts: threadTexts ?? this.threadTexts,
            });
        },
        changeCRMView(value) {
            this.settingsLoading = true;

            const code = encodeInboxSettings({
                crmMode: value,
                unreadOnly: this.unreadOnly,
                draftsOnly: this.draftsOnly,
                threadTexts: this.threadTexts,
            });

            this.$inertia.get(
                route("conversation.show"),
                { in: this.getShortCode({ crmMode: value }) },
                {
                    preserveState: false,
                    preserveScroll: false,
                    onFinish: () => {
                        this.settingsLoading = false;
                    },
                }
            );
        },
        changeUnreadOnly(value) {
            this.settingsLoading = true;

            this.$inertia.get(
                route("conversation.show"),
                { in: this.getShortCode({ unreadOnly: value }) },
                {
                    preserveState: false,
                    preserveScroll: false,
                    onFinish: () => {
                        this.settingsLoading = false;
                    },
                }
            );
        },
        changeDraftsOnly(value) {
            this.settingsLoading = true;

            this.$inertia.get(
                route("conversation.show"),
                { in: this.getShortCode({ draftsOnly: value }) },
                {
                    preserveState: false,
                    preserveScroll: false,
                    onFinish: () => {
                        this.settingsLoading = false;
                    },
                }
            );
        },
        changeThreadTexts(value) {
            this.settingsLoading = true;

            this.$inertia.reload({
                data: {
                    in: this.getShortCode({
                        threadTexts: value,
                    }),
                },
                onFinish: () => {
                    this.settingsLoading = false;
                },
            });
        },
        getClientFromParticipant(event) {
            const validParticipants = event.participants.filter(
                (e) => !e.lobby
            );

            if (validParticipants.length > 1 || !validParticipants.length) {
                console.log("invalid participants");
                return;
            }

            const participant = validParticipants[0];

            if (!participant) {
                console.log("invalid participants");
                return;
            }

            return participant;
        },
        /**
         * @param event
         * @param openCrm - Toolbar button only, avoiding auto opening on mobile from conversation select
         */
        handleSelectConversation(event, openCrm = false) {
            // Clear selected thread when changing conversations
            if (this.selectedThread?.id) {
                this.selectedThread = null;
            }

            // Gate for creating new messages
            if (event?.create && this.can("Use CRM")) {
                this.closeCrm();
            }

            // Handle CRM
            if (!this.crmMode) return;
            if (!event?.id) return;

            // For mobile users, only open the side panel if the toolbar button is pressed
            if (this.$vuetify.breakpoint.smAndDown && !openCrm) return;

            const participant = this.getClientFromParticipant(event);

            if (!participant) return;

            if (!this.crmClient?.id) {
                this.getCrmClient({
                    id: participant.messagable_id,
                    type: participant.messagable_type,
                });
                return;
            }

            // If the selected conversation is the same as the current crmClient, don't fetch again
            if (
                this.crmClient.id != participant.messagable_id ||
                this.crmClient.type != participant.messagable_type
            ) {
                // Just show loading, crmDrawer will handle the rest
                this.getCrmClient({}, true);
            }
            return;

            /* const participant = this.getClientFromParticipant(event);

            return this.getCrmClient({
                id: participant.messagable_id,
                type: participant.messagable_type,
            });

            if (event.id == this.selectedConversation?.id && !forceMobile) {
                return;
            }


            if (!this.conversation?.id && this.$vuetify.breakpoint.mdAndUp) {
                if (participant.messagable_id == this.crmClient.id) return;
                this.getCrmClient({}, true);
                return;
            }

            if (this.crmClient?.id && !forceFetch) {
                this.getCrmClient({}, true);
                return;
            }

            return this.getCrmClient({
                id: participant.messagable_id,
                type: participant.messagable_type,
            });*/
        },
    },
    /*watch: {
        conversation: {
            handler(conversation) {
                if (!conversation?.id) return;
                this.selectedConversation = conversation;
            },
            deep: true,
            //immediate: true,
        },
    },*/
};
</script>

<style>
#chat-window {
    height: 100%;
    display: flex;
    flex: 1;
    position: relative;
    flex-direction: column;
}

#chat-index {
    height: 100%;
    display: flex;
    flex: 1;
    position: relative;
    flex-direction: column;
}

.scrollbar-hidden::-webkit-scrollbar {
    display: none;
}

.chat-index-col {
    min-width: 350px;
    max-width: 350px;
    padding-top: 0px !important;
    /*border-right: solid 1px #e5e7eb;
    ::-webkit-scrollbar {
        display: none;
    }*/

    @media screen and (max-width: 960px) {
        max-width: 100%;
    }
}

.chat-toggle-offset {
    top: -1rem;
}

.inbox-title {
    min-width: 332px;
    max-width: 332px;
}

.inbox-header-button {
    width: 36px !important;
    height: 36px !important;
}

.gradient-title-text {
    background: linear-gradient(90deg, #228cbd, #f38c68);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: rgba(34, 140, 189, 0.25) 0px 4px 4px;
}

.hide-badge .v-badge__badge {
    display: none;
}
</style>
